import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { CelebrationOverlay } from "@/components/brand/CoinMascot";
import { AutoSave } from "@/components/dashboard/AutoSave";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useHousehold } from "@/context/HouseholdContext";
import { useGoals } from "@/hooks/use-goals";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const GoalForm = ({ goal, onSubmit }) => {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); onSubmit({ name, amount: Number(amount) }); }}>
      {!goal && <div><Label htmlFor="goal-name">Nom de l'objectif</Label><Input id="goal-name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} placeholder="Ex. Vacances d'été" data-testid="goal-name-input" /></div>}
      <div>
        <Label htmlFor="goal-amount">{goal ? "Montant mis de côté" : "Montant cible"}</Label>
        <Input id="goal-amount" type="number" min="1" step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="goal-amount-input" />
      </div>
      <Button type="submit" className="w-full rounded-full" data-testid="goal-submit-button">{goal ? "Ajouter à l'objectif" : "Créer l'objectif"}</Button>
    </form>
  );
};

const GoalRow = ({ g, onAdd }) => {
  const pct = Math.min(100, Math.round((g.current / g.target) * 100));
  const { money } = useFormat();
  return (
    <div data-testid={`goal-row-${g.id}`}>
      <div className="mb-1.5 flex items-center justify-between gap-2 text-sm">
        <span className="min-w-0 truncate">{g.name}</span>
        {g.reached ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-mint px-2 py-0.5 text-[10px] font-bold text-navy" data-testid={`goal-reached-${g.id}`}><Check className="h-3 w-3" />Atteint</span>
        ) : (
          <button onClick={() => onAdd(g)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-card/80 transition-transform hover:scale-110" aria-label="Ajouter un montant" data-testid={`goal-contribute-${g.id}`}><Plus className="h-3.5 w-3.5" /></button>
        )}
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-gradient-to-r from-sage to-mint transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground"><span className="num">{money(g.current)} / {money(g.target)}</span><span className="num">{pct}%</span></div>
    </div>
  );
};

export const GoalsWidget = ({ data }) => {
  const { active } = useHousehold();
  const { money } = useFormat();
  const qc = useQueryClient();
  const { data: goals, isError } = useGoals();
  const [dialog, setDialog] = useState(null); // {goal} | {create:true}
  const [party, setParty] = useState(null);

  if (isError) return <EmptyState compact icon={Lock} title="Objectifs privés" text="Votre rôle ne donne pas accès à un espace financier." testid="goals-locked" />;

  const submit = async ({ name, amount }) => {
    try {
      if (dialog.goal) {
        const { data: r } = await api.post(`/households/${active.id}/goals/${dialog.goal.id}/contributions`, { amount });
        if (r.just_reached) setParty(r.goal);
        else toast.success(`${money(amount)} ajoutés à « ${r.goal.name} »`);
      } else {
        await api.post(`/households/${active.id}/goals`, { name, target: amount });
        toast.success("Objectif créé");
      }
      setDialog(null);
      qc.invalidateQueries({ queryKey: ["goals", active.id] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="goals-widget">
      {data?.finance && (
        <div className="flex items-baseline justify-between">
          <span className="num text-2xl font-bold">{money(data.finance.savings_total)}</span>
          <span className="text-xs font-semibold text-success">+{money(data.finance.savings_month)} ce mois</span>
        </div>
      )}
      {goals?.length ? goals.map((g) => <GoalRow key={g.id} g={g} onAdd={(goal) => setDialog({ goal })} />)
        : goals && <p className="text-sm text-muted-foreground">Aucun objectif pour l'instant.</p>}
      {goals?.length > 0 && <AutoSave goals={goals} />}
      <Button variant="ghost" size="sm" className="-ml-2 rounded-full" onClick={() => setDialog({ create: true })} data-testid="goal-create-button"><Plus className="mr-1 h-4 w-4" />Nouvel objectif</Button>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="goal-dialog">
          <DialogHeader><DialogTitle className="font-heading">{dialog?.goal ? dialog.goal.name : "Nouvel objectif d'épargne"}</DialogTitle></DialogHeader>
          {dialog && <GoalForm goal={dialog.goal} onSubmit={submit} />}
        </DialogContent>
      </Dialog>
      <CelebrationOverlay open={!!party} title="Objectif atteint !" onClose={() => setParty(null)} cta="Bravo, merci !"
        text={party ? `Vous avez réuni ${money(party.current)} pour « ${party.name} ». Votre pièce CASHMY est fière de vous.` : ""} />
    </div>
  );
};
